import React, { useEffect, useState } from 'react';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack,
  Grid, MenuItem, TextField, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, Paper, InputAdornment, useMediaQuery, Snackbar, Alert, FormControl, InputLabel, CircularProgress, Checkbox, FormControlLabel
} from '@mui/material';
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { tokens } from "../../../../../theme.js";
import TextareaAutosize from '@mui/material/TextareaAutosize';
import axios from 'axios';
import { useTheme } from "@mui/material/styles";
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';

const jobTypes = ['FullTime', 'Interns', 'PartTime'];
const jobLocations = ['Pune', 'Ahmedabad', 'Pune/Ahmedabad', 'Remote'];
const jobStatusOptions = ['New', 'Published', 'Closed'];

const AddJob = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [isLoading, setIsLoading] = useState(true);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [viewData, setViewData] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [successOpen, setSuccessOpen] = useState(false);

  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [open, setOpen] = useState(false);
  const [jobRoles, setJobRoles] = useState([]);
  const [formData, setFormData] = useState({
    JobTitle: '', Location: '', JobType: '', JobRole: '',
    JobAbout: '', JobResponsibility: [], JobSkills: [],
    CreatedBy: '', Status: 'New', isdrive: false, ClosingDate: '',
    JobLink: '', ExternalApplication: false, orderBy: ''
  });

  const [filters, setFilters] = useState({
    search: '', type: '', location: '', status: ''
  });
  const accessToken = JSON.parse(localStorage.getItem('emsUser'))?.user?.stsTokenManager?.accessToken;

  const fetchJobs = async (token) => {
    try {
      setIsLoading(true);
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL_HRM}/get_all_jobs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setJobs(res.data || []);
      setFilteredJobs(res.data || []);
      // console.log("Jobs fetched successfully:", res.data);
    } catch (err) {
      console.error("Failed to fetch jobs:", err);
      setJobs([]);
      setFilteredJobs([]);
      setAlertMessage("Failed to load jobs. Please try again.");
      setAlertOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchJobRoles = async (token) => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL_HRM}/job_roles`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setJobRoles(res.data || ['DevOps Engineer', 'FullStack Engineer', 'Frontend Developer']);
    } catch (err) {
      console.error("Failed to fetch job roles:", err);
      setJobRoles(['DevOps Engineer', 'FullStack Engineer', 'Frontend Developer']);
    }
  };

  // Handle auth state and initialization
  useEffect(() => {
    let unsubscribe;
    const auth = getAuth();

    const initializeForm = async (user) => {
      if (!user) {
        console.error("No authenticated user found.");
        setIsLoading(false);
        return;
      }

      const userEmail = user.email;
      const accessToken = await user.getIdToken(); // Get fresh token
      localStorage.setItem('emsUser', JSON.stringify({ user: { stsTokenManager: { accessToken } } })); // Store updated token

      setFormData((prev) => ({
        ...prev,
        CreatedBy: userEmail,
      }));

      await fetchJobs(accessToken);
      await fetchJobRoles(accessToken);
    };

    unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        initializeForm(user);
      } else {
        console.error("User not authenticated. Redirecting to login might be needed.");
        setIsLoading(false);
      }
    });

    return () => unsubscribe && unsubscribe(); // Cleanup subscription
  }, []); // Runs once on mount

  const handleView = (job) => {
    setViewData(job);
    setViewOpen(true);
  };
  const handleEdit = (job) => {
    // Helper function to properly format the comma-separated data to semicolon-separated
    const formatToSemicolon = (data) => {
      if (!data) return '';

      if (Array.isArray(data)) {
        return data.map(item => item.replace(/[,;]\s*$/, '').trim()).join('; ');
      }

      // Handle string data - split by pattern that indicates new items
      // Items typically end with period or comma followed by capital letter
      let cleanData = data.replace(/\.\s*;/g, ';').replace(/,\s*;/g, ';');

      // Split by semicolon first, then by comma+capital letter pattern
      let items = cleanData.split(/[;,]\s*(?=[A-Z])|[;,]\s*$/)
        .map(item => item.trim())
        .filter(item => item !== '')
        .map(item => item.replace(/[,;.]\s*$/, '').trim()); // Remove trailing punctuation

      return items.join('; ');
    };

    const formatToDate = (date) => {
      if (!date) return null;
      let d;
      if (date.seconds) {
        d = new Date(date.seconds * 1000);
      } else {
        d = new Date(date);
      }
      return isNaN(d.getTime()) ? null : d;
    };

    setFormData({
      ID: job.ID,
      JobTitle: job.JobTitle,
      Location: job.Location,
      JobType: job.JobType,
      JobRole: job.JobRole,
      JobAbout: job.JobAbout,
      JobResponsibility: formatToSemicolon(job.JobResponsibility),
      JobSkills: formatToSemicolon(job.JobSkills),
      CreatedBy: job.CreatedBy,
      Status: job.Status,
      isdrive: job.isdrive || false,
      isdrive: job.isdrive || false,
      ClosingDate: formatToDate(job.ClosingDate),
      JobLink: job.JobLink || '',
      ExternalApplication: job.ExternalApplication || false,
      orderBy: job.orderBy !== undefined && job.orderBy !== null ? job.orderBy : '',
    });
    setEditMode(true);
    setOpen(true);
  };
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this job?")) return;
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL_HRM}/delete_job/${id}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`
          }
        }
      );
      fetchJobs();
    } catch (err) {
      console.error('Delete failed:', err);
      setAlertMessage('Failed to delete job.');
      setAlertOpen(true);
    }
  };


  const handleOpen = () => setOpen(true);

  const handleClose = () => {
    setOpen(false);
    setEditMode(false);
    setFormData({
      JobTitle: '', Location: '', JobType: '', JobRole: '',
      JobAbout: '', JobResponsibility: [], JobSkills: [],
      CreatedBy: formData.CreatedBy, Status: 'New', isdrive: false, ClosingDate: null,
      JobLink: '', ExternalApplication: false, orderBy: ''
    });
  };


  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => {
      if (type === 'checkbox') {
        return { ...prev, [name]: checked };
      }
      if (name === 'orderBy') {
        return { ...prev, [name]: value === '' ? '' : Number(value) };
      }
      return { ...prev, [name]: value };
    });
  };


  const handleSubmit = async () => {
    // Validate required fields (excluding conditional JobLink)
    const requiredFields = ['JobTitle', 'Location', 'JobType', 'JobRole', 'JobAbout', 'JobResponsibility', 'JobSkills', 'CreatedBy', 'Status'];
    const isRequiredEmpty = requiredFields.some(field => {
      const value = formData[field];
      if (Array.isArray(value)) {
        return value.length === 0;
      }
      return !value || value === '';
    });
    // If external application is checked, ensure JobLink is provided
    const isJobLinkEmpty = formData.ExternalApplication && (!formData.JobLink || formData.JobLink.trim() === '');
    if (isRequiredEmpty || isJobLinkEmpty) {
      setAlertMessage('Please fill all the mandatory fields before submitting.');
      setAlertOpen(true);
      return;
    }


    try {
      if (editMode) {
        await axios.put(
          `${process.env.REACT_APP_BACKEND_URL_HRM}/update_job/${formData.ID}`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`
            }
          }
        );
        setSuccessMessage('Job updated successfully!');
      } else {
        console.log("job data:", formData);
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL_HRM}/create_job_post`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`
            }
          }
        );
        setSuccessMessage('Job created successfully!');
      }

      setSuccessOpen(true);  // trigger success snackbar
      handleClose();
      fetchJobs();
    } catch (err) {
      console.error('Failed to submit job:', err);
      setAlertMessage('Something went wrong. Please try again.');
      setAlertOpen(true);
    }

  };



  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    let updated = [...jobs];
    const { search, type, location, status } = filters;
    if (search) {
      updated = updated.filter(job =>
        job.JobTitle?.toLowerCase().includes(search.toLowerCase()) ||
        job.JobRole?.toLowerCase().includes(search.toLowerCase())
      );
    }
    if (type) updated = updated.filter(job => job.JobType === type);
    if (location) updated = updated.filter(job => job.Location === location);
    if (status) updated = updated.filter(job => job.Status === status);
    setFilteredJobs(updated);
  }, [filters, jobs]);

  if (isLoading) {
    return <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
      <CircularProgress sx={{ color: colors.grey[100] }} />
    </Box>;
  }
  return (
    <Box p={isMobile ? 2 : 4} bgcolor={theme.palette.background.default}>
      <Box
        display="flex"
        justifyContent="flex-end"
        alignItems="end"
        mb={2}
        p={2}
        borderRadius={2}

      >
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpen} sx={{
          backgroundColor: "green"
        }}>
          New Job
        </Button>
      </Box>


      {/* Filters */}
      <Paper elevation={2} sx={{ p: 2, borderRadius: 3, mb: 3, backgroundColor: theme.palette.mode === 'dark' ? '#1f2937' : '#e0e0e0', backgroundImage: 'none', }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              name="search"
              label="Search by title or role"
              value={filters.search}
              onChange={handleFilterChange}
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{
                backgroundColor: 'transparent',
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: theme.palette.mode === 'dark' ? 'white' : 'black',
                  },
                  '&:hover fieldset': {
                    borderColor: theme.palette.mode === 'dark' ? 'white' : 'black',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: theme.palette.mode === 'dark' ? 'white' : 'black',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: theme.palette.mode === 'dark' ? 'white' : 'black',
                }, '& label.Mui-focused': {
                  color: theme.palette.mode === 'dark' ? 'white' : 'black'
                },
              }}
            />

          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              select
              label="Type"
              name="type"
              value={filters.type}
              onChange={handleFilterChange}
              InputProps={{
                startAdornment: filters.type ? null : (
                  <InputAdornment position="start">
                    Select Type
                  </InputAdornment>
                ),
              }}
              sx={{
                backgroundColor: 'transparent',
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: theme.palette.mode === 'dark' ? 'white' : 'black',
                  },
                  '&:hover fieldset': {
                    borderColor: theme.palette.mode === 'dark' ? 'white' : 'black',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: theme.palette.mode === 'dark' ? 'white' : 'black',
                    color: theme.palette.mode === 'dark' ? 'white' : 'black',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: theme.palette.mode === 'dark' ? 'white' : 'black',
                },
                '& label.Mui-focused': {
                  color: theme.palette.mode === 'dark' ? 'white' : 'black',
                },
              }}
            >
              <MenuItem value="">All Types</MenuItem>
              {jobTypes.map(type => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              select
              label="Location"
              name="location"
              value={filters.location}
              onChange={handleFilterChange}
              InputProps={{
                startAdornment: filters.location ? null : (
                  <InputAdornment position="start">
                    Select Location
                  </InputAdornment>
                ),
              }}
              sx={{
                backgroundColor: 'transparent',
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: theme.palette.mode === 'dark' ? 'white' : 'black',
                  },
                  '&:hover fieldset': {
                    borderColor: theme.palette.mode === 'dark' ? 'white' : 'black',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: theme.palette.mode === 'dark' ? 'white' : 'black',
                    color: theme.palette.mode === 'dark' ? 'white' : 'black',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: theme.palette.mode === 'dark' ? 'white' : 'black',
                },
                '& label.Mui-focused': {
                  color: theme.palette.mode === 'dark' ? 'white' : 'black',
                },
              }}
            >
              <MenuItem value="">All Locations</MenuItem>
              {jobLocations.map(loc => (
                <MenuItem key={loc} value={loc}>
                  {loc}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              select
              label="Status"
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
              InputProps={{
                startAdornment: filters.status ? null : (
                  <InputAdornment position="start">
                    Select Status
                  </InputAdornment>
                ),
              }}
              sx={{
                backgroundColor: 'transparent',
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: theme.palette.mode === 'dark' ? 'white' : 'black',
                  },
                  '&:hover fieldset': {
                    borderColor: theme.palette.mode === 'dark' ? 'white' : 'black',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: theme.palette.mode === 'dark' ? 'white' : 'black',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: theme.palette.mode === 'dark' ? 'white' : 'black',
                },
                '& label.Mui-focused': {
                  color: theme.palette.mode === 'dark' ? 'white' : 'black',
                },
              }}
            >
              <MenuItem value="">All Status</MenuItem>
              {jobStatusOptions.map(status => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {/* Jobs Table */}
      <Paper elevation={3} sx={{ borderRadius: 3, overflowX: 'auto' }}>
        <Table>
          <TableHead>
            <TableRow
              sx={{ backgroundColor: theme.palette.mode === 'dark' ? '#1f2937' : '#e0e0e0' }}
            >
              {['Job Title', 'Location', 'Type', 'Role', 'Status', 'Closing Date', 'Job Order', 'Created By', 'Actions'].map((header) => (
                <TableCell
                  key={header}
                  sx={{
                    color: theme.palette.mode === 'dark' ? 'white' : 'black',
                    fontWeight: 'bold',
                  }}
                >
                  {header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {filteredJobs.length > 0 ? (
              filteredJobs.map((job) => (
                <TableRow
                  key={job.ID}
                  sx={{
                    backgroundColor: theme.palette.mode === 'dark' ? '#111827' : 'white',

                  }}
                >
                  <TableCell sx={{ color: theme.palette.mode === 'dark' ? 'white' : 'black' }}>{job.JobTitle}</TableCell>
                  <TableCell sx={{ color: theme.palette.mode === 'dark' ? 'white' : 'black' }}>{job.Location}</TableCell>
                  <TableCell sx={{ color: theme.palette.mode === 'dark' ? 'white' : 'black' }}>{job.JobType}</TableCell>
                  <TableCell sx={{ color: theme.palette.mode === 'dark' ? 'white' : 'black' }}>{job.JobRole}</TableCell>
                  <TableCell sx={{ color: theme.palette.mode === 'dark' ? 'white' : 'black' }}>{job.Status}</TableCell>
                  <TableCell sx={{ color: theme.palette.mode === 'dark' ? 'white' : 'black' }}>
                    {job.ClosingDate
                      ? (job.ClosingDate.seconds
                        ? new Date(job.ClosingDate.seconds * 1000).toLocaleDateString('en-IN')
                        : new Date(job.ClosingDate).toLocaleDateString('en-IN'))
                      : '-'}
                  </TableCell>
                  <TableCell sx={{ color: theme.palette.mode === 'dark' ? 'white' : 'black' }}>{job.orderBy ?? '-'}</TableCell>

                  <TableCell sx={{ color: theme.palette.mode === 'dark' ? 'white' : 'black' }}>{job.CreatedBy}</TableCell>

                  <TableCell>
                    <Button
                      size="small"
                      onClick={() => handleView(job)}
                      sx={{
                        backgroundColor: colors.blueAccent[600],
                        color: "#fff",
                        "&:hover": {
                          backgroundColor: colors.blueAccent[700],
                        },
                      }}
                    >
                      View
                    </Button>

                    <Button
                      size="small"
                      onClick={() => handleEdit(job)}
                      sx={{
                        margin: "10px",
                        backgroundColor: colors.greenAccent[600],
                        color: "#fff",
                        "&:hover": {
                          backgroundColor: colors.greenAccent[700],
                        },
                      }}
                    >
                      Edit
                    </Button>

                    <Button
                      size="small"
                      onClick={() => handleDelete(job.ID)}
                      sx={{
                        backgroundColor: colors.redAccent[600],
                        color: "#fff",
                        "&:hover": {
                          backgroundColor: colors.redAccent[700],
                        },
                      }}
                    >
                      Delete
                    </Button>

                  </TableCell>


                </TableRow>
              ))
            ) : (
              <TableRow
                sx={{
                  backgroundColor: theme.palette.mode === 'dark' ? '#111827' : 'white',
                }}
              >
                <TableCell
                  colSpan={6}
                  align="center"
                  sx={{ color: theme.palette.mode === 'dark' ? 'white' : 'black' }}
                >
                  No jobs found
                </TableCell>
              </TableRow>
            )}
          </TableBody>

        </Table>
      </Paper>
      <Dialog open={viewOpen} onClose={() => setViewOpen(false)} maxWidth="md" fullWidth>
        {/* Title */}
        <DialogTitle
          sx={{
            backgroundColor: theme.palette.mode === 'dark' ? '#1e293b' : theme.palette.background.paper,
            color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit',
            fontWeight: 600,
          }}
        >
          Job Overview
        </DialogTitle>

        {/* Content */}
        <DialogContent
          dividers
          sx={{
            backgroundColor: theme.palette.mode === 'dark' ? '#0f172a' : theme.palette.background.default,
            px: 4,
            py: 3,
          }}
        >
          {viewData ? (
            <Stack spacing={3}>
              {/* Basic Info */}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Title</Typography>
                  <Typography variant="body1" fontWeight={500}>{viewData.JobTitle || '-'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Role</Typography>
                  <Typography variant="body1" fontWeight={500}>{viewData.JobRole || '-'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Type</Typography>
                  <Typography variant="body1" fontWeight={500}>{viewData.JobType || '-'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Location</Typography>
                  <Typography variant="body1" fontWeight={500}>{viewData.Location || '-'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                  <Typography variant="body1" fontWeight={500}>{viewData.Status || '-'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Closing Date</Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {viewData.ClosingDate
                      ? (viewData.ClosingDate.seconds
                        ? new Date(viewData.ClosingDate.seconds * 1000).toLocaleString('en-IN', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: true
                        })
                        : new Date(viewData.ClosingDate).toLocaleString('en-IN', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: true
                        }))
                      : '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Created By</Typography>
                  <Typography variant="body1" fontWeight={500}>{viewData.CreatedBy || '-'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Job Order No.</Typography>
                  <Typography variant="body1" fontWeight={500}>{viewData.orderBy ?? '-'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Is Drive?</Typography>
                  <Typography variant="body1" fontWeight={500}>{viewData.isdrive ? 'Yes' : 'No'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">External Application?</Typography>
                  <Typography variant="body1" fontWeight={500}>{viewData.ExternalApplication ? 'Yes' : 'No'}</Typography>
                </Grid>
                {viewData.ExternalApplication && viewData.JobLink && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">Job Link</Typography>
                    <a href={viewData.JobLink} target="_blank" rel="noopener noreferrer" style={{ color: colors.greenAccent[500], textDecoration: 'underline' }}>
                      {viewData.JobLink}
                    </a>
                  </Grid>
                )}
              </Grid>

              {/* Description Sections */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>About the Job</Typography>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>{viewData.JobAbout || '-'}</Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>Responsibilities</Typography>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>{viewData.JobResponsibility || '-'}</Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>Required Skills</Typography>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>{viewData.JobSkills || '-'}</Typography>
              </Box>
            </Stack>
          ) : (
            <Typography>Loading job details...</Typography>
          )}
        </DialogContent>

        {/* Actions */}
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            backgroundColor: theme.palette.mode === 'dark' ? '#1e293b' : theme.palette.background.paper,
          }}
        >
          <Button
            onClick={() => setViewOpen(false)}
            variant="contained"
            sx={{
              backgroundColor: theme.palette.mode === 'dark' ? '#22c55e' : '#16a34a',
              color: '#fff',
              textTransform: 'none',
              fontWeight: 500,
              px: 3,
              '&:hover': {
                backgroundColor: theme.palette.mode === 'dark' ? '#16a34a' : '#15803d',
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>



      {/* Create Job Dialog */}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
        <DialogTitle
          sx={{
            backgroundColor: theme.palette.mode === 'dark' ? '#1e293b' : theme.palette.background.paper,
            color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit',
          }}
        >
          {editMode ? 'Update Job' : 'Create New Job'}
        </DialogTitle>


        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DialogContent
            dividers
            sx={{
              backgroundColor: theme.palette.mode === 'dark' ? '#0f172a' : theme.palette.background.default,
            }}
          >
            <Grid container spacing={2} mt={1}>
              {[
                { name: 'JobTitle', label: 'Job Title', type: 'text' },
                { name: 'Location', label: 'Location', type: 'select', options: jobLocations },
                { name: 'JobType', label: 'Job Type', type: 'select', options: jobTypes },
                { name: 'JobRole', label: 'Job Role', type: 'select', options: jobRoles },
                { name: 'JobAbout', label: 'About the Job', type: 'textarea' },
                {
                  name: 'JobResponsibility',
                  label: 'Job Responsibilities',
                  type: 'textarea',
                  note: true,
                  noteMessage: 'Enter responsibilities as a semicolon-separated list (e.g., Responsibility 1; Responsibility 2).',
                  example: 'Responsibility 1;\nResponsibility 2;\nResponsibility 3;'
                },
                {
                  name: 'JobSkills',
                  label: 'Required Skills',
                  type: 'textarea',
                  note: true,
                  noteMessage: 'Enter skills as a semicolon-separated list (e.g., Skill 1; Skill 2).',
                  example: 'Skill 1;\nSkill 2;\nSkill 3;'
                },
                { name: 'Status', label: 'Status', type: 'select', options: jobStatusOptions },
                { name: 'ClosingDate', label: 'Closing Date', type: 'date' },
                { name: 'orderBy', label: 'Job Order No.', type: 'number' }
              ].map((field, idx) => (
                <Grid key={idx} item xs={12} md={field.type === 'textarea' ? 12 : 6}>
                  {field.type === 'date' ? (
                    <DateTimePicker
                      label="Closing Date"
                      value={formData.ClosingDate ? dayjs(formData.ClosingDate) : null}
                      onChange={(newValue) => {
                        setFormData((prev) => ({
                          ...prev,
                          ClosingDate: newValue ? newValue.toDate() : null,
                        }));
                      }}
                      disablePast
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          margin: 'normal',
                          size: 'medium',
                          InputLabelProps: {
                            shrink: true,
                            sx: {
                              color:
                                theme.palette.mode === 'dark' ? '#ffffff' : 'inherit',
                            },
                          },
                          InputProps: {
                            sx: {
                              color:
                                theme.palette.mode === 'dark' ? '#fff' : 'inherit',
                            },
                          },
                        },
                      }}
                    />
                  ) : field.type === 'textarea' ? (


                    <FormControl fullWidth margin="normal">
                      <Typography
                        variant="subtitle2"
                        sx={{ color: theme.palette.mode === 'dark' ? '#ffffff' : '#000000', mb: 1 }}
                      >
                        {field.label}
                      </Typography>
                      <TextareaAutosize
                        minRows={5}
                        name={field.name}
                        value={formData[field.name]}
                        onChange={handleChange}
                        placeholder={`Enter ${field.label.toLowerCase()}`}
                        style={{
                          width: '100%',
                          padding: '12px',
                          borderRadius: '6px',
                          border: `1px solid ${theme.palette.mode === 'dark' ? '#555' : '#ccc'}`,
                          backgroundColor: 'transparent',
                          color: theme.palette.mode === 'dark' ? '#fff' : '#000',
                          fontSize: '0.95rem',
                          resize: 'vertical',
                          fontFamily: theme.typography.fontFamily,
                        }}
                      />
                      {field.note && (
                        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography
                            variant="caption"
                            sx={{ color: theme.palette.mode === 'dark' ? '#aaaaaa' : '#666666' }}
                          >
                            NOTE: {field.noteMessage}
                          </Typography>
                        </Box>
                      )}
                    </FormControl>
                  ) : (
                    <TextField
                      fullWidth
                      name={field.name}
                      label={field.label}
                      value={formData[field.name]}
                      onChange={handleChange}
                      type={field.type === 'number' ? 'number' : 'text'}
                      select={field.type === 'select'}
                      variant="outlined"
                      margin="normal"
                      InputLabelProps={{
                        shrink: true,
                        sx: {
                          color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit',
                          fontFamily: theme.typography.fontFamily,
                        },
                      }}
                      InputProps={{
                        sx: {
                          backgroundColor: 'transparent',
                          color: theme.palette.mode === 'dark' ? '#fff' : 'inherit',
                          fontFamily: theme.typography.fontFamily,
                        },
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: theme.palette.mode === 'dark' ? 'grey' : '#ced4da',
                          },
                          '&:hover fieldset': {
                            borderColor: theme.palette.mode === 'dark' ? 'grey' : '#000',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#ffffff',
                          },
                        },
                        '& label.Mui-focused': {
                          color: '#ffffff',
                        },
                      }}
                    >
                      {field.options &&
                        field.options.map((opt) => (
                          <MenuItem key={opt} value={opt}>
                            {opt}
                          </MenuItem>
                        ))}
                    </TextField>
                  )
                  }
                </Grid>
              ))}
              <Grid item xs={12}>
                <Stack direction="row" spacing={2}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.isdrive}
                        onChange={handleChange}
                        name="isdrive"
                        sx={{
                          color: theme.palette.mode === 'dark' ? '#fff' : 'inherit',
                          '&.Mui-checked': {
                            color: theme.palette.mode === 'dark' ? '#22c55e' : 'green',
                          },
                        }}
                      />
                    }
                    label={
                      <Typography
                        sx={{ color: theme.palette.mode === 'dark' ? '#fff' : 'inherit' }}
                      >
                        Drive-based openings
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.ExternalApplication}
                        onChange={handleChange}
                        name="ExternalApplication"
                        sx={{
                          color: theme.palette.mode === 'dark' ? '#fff' : 'inherit',
                          '&.Mui-checked': {
                            color: theme.palette.mode === 'dark' ? '#22c55e' : 'green',
                          },
                        }}
                      />
                    }
                    label={
                      <Typography
                        sx={{ color: theme.palette.mode === 'dark' ? '#fff' : 'inherit' }}
                      >
                        Is this external application?
                      </Typography>
                    }
                  />
                </Stack>
                {formData.ExternalApplication && (
                  <Box mt={2}>
                    <TextField
                      fullWidth
                      name="JobLink"
                      label="Job Link"
                      value={formData.JobLink}
                      onChange={handleChange}
                      variant="outlined"
                      InputLabelProps={{
                        shrink: true,
                        sx: {
                          color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit',
                          fontFamily: theme.typography.fontFamily,
                        },
                      }}
                      InputProps={{
                        sx: {
                          backgroundColor: 'transparent',
                          color: theme.palette.mode === 'dark' ? '#fff' : 'inherit',
                          fontFamily: theme.typography.fontFamily,
                        },
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: theme.palette.mode === 'dark' ? 'grey' : '#ced4da',
                          },
                          '&:hover fieldset': {
                            borderColor: theme.palette.mode === 'dark' ? 'grey' : '#000',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#ffffff',
                          },
                        },
                        '& label.Mui-focused': {
                          color: '#ffffff',
                        },
                      }}
                    />
                  </Box>
                )}
              </Grid>

            </Grid>
          </DialogContent>
        </LocalizationProvider>


        <DialogActions
          sx={{
            p: 2,
            backgroundColor: theme.palette.mode === 'dark' ? '#1e293b' : theme.palette.background.paper,
          }}
        >
          <Button onClick={handleClose} color="secondary">
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmit} sx={{ backgroundColor: "green" }}>
            {editMode ? 'Update Job' : 'Create Job'}
          </Button>

        </DialogActions>
      </Dialog>
      <Snackbar
        open={successOpen}
        autoHideDuration={3000}
        onClose={() => setSuccessOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccessOpen(false)} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={alertOpen}
        autoHideDuration={4000}
        onClose={() => setAlertOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setAlertOpen(false)} severity="warning" sx={{ width: '100%' }}>
          {alertMessage}
        </Alert>
      </Snackbar>

    </Box >
  );
};



export default AddJob;
